import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../lib/utils';

type Color = 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';
type Position = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
type Size = 'sm' | 'md' | 'lg';

// ── Variants ──────────────────────────────────────────────────────────────────

const badgeVariants = tv({
  slots: {
    root: 'relative inline-flex',
    indicator: [
      'absolute z-10 flex items-center justify-center rounded-full font-semibold',
      'ring-2 ring-white dark:ring-uniform-gray-900'
    ]
  },
  variants: {
    position: {
      'top-right': { indicator: '-top-1.5 -right-1.5' },
      'top-left': { indicator: '-top-1.5 -left-1.5' },
      'bottom-right': { indicator: '-bottom-1.5 -right-1.5' },
      'bottom-left': { indicator: '-bottom-1.5 -left-1.5' }
    },
    color: {
      neutral: { indicator: 'bg-uniform-gray-600 text-white dark:bg-uniform-gray-500' },
      primary: { indicator: 'bg-green-600 text-white dark:bg-green-500' },
      secondary: { indicator: 'bg-blue-600 text-white dark:bg-blue-500' },
      destructive: { indicator: 'bg-uniform-red-600 text-white dark:bg-uniform-red-500' },
      warning: { indicator: 'bg-yellow-500 text-yellow-950 dark:bg-yellow-400' }
    },
    size: {
      sm: { indicator: 'h-4 min-w-4 px-0.5 text-[9px]' },
      md: { indicator: 'h-5 min-w-5 px-1 text-[10px]' },
      lg: { indicator: 'h-6 min-w-6 px-1 text-xs' }
    },
    dot: {
      true: { indicator: '!h-2.5 !w-2.5 !min-w-0 !p-0' }
    }
  },
  defaultVariants: {
    position: 'top-right',
    color: 'destructive',
    size: 'md',
    dot: false
  }
});

type BadgeVariants = VariantProps<typeof badgeVariants>;

// ── Component ─────────────────────────────────────────────────────────────────

interface BadgeProps {
  /** Content that the badge overlays */
  children?: ReactNode;
  color?: Color;
  position?: Position;
  size?: Size;
  /** Show a simple colored dot with no content */
  dot?: boolean;
  /** Icon shown inside the badge indicator */
  icon?: IconDefinition;
  /** Number shown inside the badge indicator. Values above `max` render as `{max}+` */
  count?: number;
  /** Cap for count display. Defaults to 99 */
  max?: number;
  /** Hide the badge indicator entirely */
  hidden?: boolean;
  className?: string;
}

function Badge({
  children,
  color = 'destructive',
  position = 'top-right',
  size = 'md',
  dot = false,
  icon,
  count,
  max = 99,
  hidden = false,
  className
}: BadgeProps) {
  const { root, indicator } = badgeVariants({ color, position, size, dot });

  const label =
    dot || hidden
      ? null
      : icon
        ? null
        : count != null
          ? count > max
            ? `${max}+`
            : String(count)
          : null;

  return (
    <span className={cn(root(), className)}>
      {children}
      {!hidden && (
        <span className={indicator()}>
          {!dot && icon && <FontAwesomeIcon icon={icon} className="h-[0.6em] w-[0.6em]" />}
          {label}
        </span>
      )}
    </span>
  );
}

export { Badge, badgeVariants };
export type { BadgeProps, BadgeVariants };
